import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BRAND, NAV_LINKS } from "../data/brand";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <img
      src="/assets/logo-nexawash.png"
      alt="NexaWash Studio"
      className="h-11 md:h-12 w-auto object-contain transition-transform group-hover:scale-105"
      data-testid="nav-logo-img"
    />
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    // If already on home, just scroll; otherwise navigate to home then scroll
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToPage = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || location.pathname !== "/"
          ? "bg-black/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) =>
            l.type === "page" ? (
              <Link
                key={l.id}
                to={l.path}
                data-testid={`nav-link-${l.id}`}
                className="text-sm font-medium text-nexa-muted hover:text-white transition-colors tracking-wide"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.id}
                onClick={() => goTo(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-sm font-medium text-nexa-muted hover:text-white transition-colors tracking-wide"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => goTo("preventivo")}
            data-testid="nav-cta-preventivo"
            className="hidden md:inline-flex btn-primary"
          >
            Preventivo
          </button>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-black/95 backdrop-blur-xl" data-testid="mobile-menu-panel">
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) =>
              l.type === "page" ? (
                <button
                  key={l.id}
                  onClick={() => goToPage(l.path)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="text-left py-3 px-2 text-sm text-nexa-muted hover:text-nexa-blue border-b border-white/5 uppercase tracking-wider"
                >
                  {l.label}
                </button>
              ) : (
                <button
                  key={l.id}
                  onClick={() => goTo(l.id)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="text-left py-3 px-2 text-sm text-nexa-muted hover:text-nexa-blue border-b border-white/5 uppercase tracking-wider"
                >
                  {l.label}
                </button>
              )
            )}
            <a
              href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
              className="mt-2 py-3 px-2 text-sm text-nexa-blue font-semibold"
              data-testid="mobile-nav-phone"
            >
              {BRAND.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
